import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useDealStore } from '@/stores/deal-store'
import { useCriteriaStore } from '@/stores/criteria-store'
import { calculateDealMetrics, evaluateGoNoGo } from '@/lib/schema'
import { DealMetrics } from '@/components/deals/DealMetrics'
import { GoNoGoVerdict } from '@/components/deals/GoNoGoVerdict'
import { DealForm } from '@/components/deals/DealForm'
import type { DealInput } from '@/lib/schema'
import { ArrowLeft, Pencil, Trash2, X } from 'lucide-react'

export function DealDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentDeal, isLoading, error, getDeal, updateDeal, deleteDeal } = useDealStore()
  const { criteria } = useCriteriaStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (id) {
      getDeal(id)
    }
  }, [id, getDeal])

  const handleUpdate = async (data: DealInput) => {
    if (!id) return
    setIsSubmitting(true)
    try {
      await updateDeal(id, data)
      setIsEditing(false)
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteDeal(id)
      navigate('/deals')
    } catch {
      // Error handled by store
    }
  }

  if (isLoading || !currentDeal) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-danger-200 bg-danger-50 p-6 text-center">
        <p className="text-danger-700">{error}</p>
        <Link
          to="/deals"
          className="mt-4 inline-block rounded-lg bg-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          Back to Deals
        </Link>
      </div>
    )
  }

  const metrics = calculateDealMetrics(currentDeal)
  const goNoGo = criteria ? evaluateGoNoGo(metrics, criteria) : null

  if (isEditing) {
    return (
      <div>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              onClick={() => setIsEditing(false)}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
              Cancel Edit
            </button>
            <h1 className="mt-4">Edit Deal</h1>
          </div>
        </div>

        <DealForm
          initialData={currentDeal}
          onSubmit={handleUpdate}
          isSubmitting={isSubmitting}
          submitLabel="Save Changes"
        />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/deals"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Deals
        </Link>

        <div className="mt-4 flex items-start justify-between">
          <div>
            <h1>{currentDeal.name || currentDeal.address}</h1>
            <p className="mt-1 text-slate-500">
              {currentDeal.address}, {currentDeal.city}, {currentDeal.state} {currentDeal.zip}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 rounded-lg border border-danger-200 bg-white px-3 py-2 text-sm font-medium text-danger-600 hover:bg-danger-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Delete Deal?</h3>
            <p className="mt-2 text-slate-500">
              This action cannot be undone. The deal will be permanently removed.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="rounded-lg bg-danger-600 px-4 py-2 text-sm font-medium text-white hover:bg-danger-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Go/No-Go Verdict */}
      {goNoGo && <GoNoGoVerdict result={goNoGo} className="mb-6" />}

      {/* Metrics */}
      <DealMetrics deal={currentDeal} metrics={metrics} />

      {/* Notes */}
      {currentDeal.notes && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-medium text-slate-900">Notes</h3>
          <p className="mt-2 whitespace-pre-wrap text-slate-600">{currentDeal.notes}</p>
        </div>
      )}
    </div>
  )
}
